import { getWorkflowRuleGroups } from "../workflow-rule-groups.ts";
import { ApiPerformanceReviewReviewerTypes } from "@/types/performance-review.types.ts";
import { ApiWorkflowBlockActionPerformanceReviewReviewerType } from "@/types/workflows/workflow.action-notification.types.ts";
import { WorkflowPerformanceReviewReviewerTypeFormData } from "./types";

export const ANONYMOUS_THRESHOLD_MIN = 3;
export const REVIEWER_TYPES_ENABLED_VALIDATION_FIELD =
  "block_data.reviewer_types_enabled_validation" as const;

export const isAnonymousSupportedReviewerType = (
  reviewerType: ApiPerformanceReviewReviewerTypes
) => {
  return (
    reviewerType !== ApiPerformanceReviewReviewerTypes.myself &&
    reviewerType !== ApiPerformanceReviewReviewerTypes.manager
  );
};

export const getReviewerTypeTitle = (
  t: (key: string) => string,
  reviewerType: ApiPerformanceReviewReviewerTypes,
  customTitle: string | null
): string => {
  if (reviewerType === ApiPerformanceReviewReviewerTypes.custom) {
    return customTitle || t("performance_review:review_type_custom");
  }
  if (reviewerType === ApiPerformanceReviewReviewerTypes.myself) {
    return t("performance_review:review_type_self");
  }
  if (reviewerType === ApiPerformanceReviewReviewerTypes.manager) {
    return t("performance_review:review_type_manager");
  }
  if (reviewerType === ApiPerformanceReviewReviewerTypes.upward) {
    return t("performance_review:review_type_upward");
  }
  return t("performance_review:review_type_peer");
};

export const getAddableReviewerTypeOptions = (
  t: (key: string) => string,
  reviewerTypes: WorkflowPerformanceReviewReviewerTypeFormData[]
) => {
  const hasSelf = reviewerTypes.some(
    (item) => item.reviewer_type === ApiPerformanceReviewReviewerTypes.myself
  );
  const hasManager = reviewerTypes.some(
    (item) => item.reviewer_type === ApiPerformanceReviewReviewerTypes.manager
  );
  const hasPeer = reviewerTypes.some(
    (item) => item.reviewer_type === ApiPerformanceReviewReviewerTypes.peer
  );
  const hasUpward = reviewerTypes.some(
    (item) => item.reviewer_type === ApiPerformanceReviewReviewerTypes.upward
  );
  const options: Array<{ id: ApiPerformanceReviewReviewerTypes; title: string }> = [];

  if (!hasSelf) {
    options.push({
      id: ApiPerformanceReviewReviewerTypes.myself,
      title: t("performance_review:review_type_self"),
    });
  }

  if (!hasManager) {
    options.push({
      id: ApiPerformanceReviewReviewerTypes.manager,
      title: t("performance_review:review_type_manager"),
    });
  }

  if (!hasUpward) {
    options.push({
      id: ApiPerformanceReviewReviewerTypes.upward,
      title: t("performance_review:review_type_upward"),
    });
  }

  if (!hasPeer) {
    options.push({
      id: ApiPerformanceReviewReviewerTypes.peer,
      title: t("performance_review:review_type_peer"),
    });
  }

  options.push({
    id: ApiPerformanceReviewReviewerTypes.custom,
    title: t("performance_review:review_type_custom") + "...",
  });

  return options;
};

export const mapReviewerTypesToForm = (
  reviewerTypes: ApiWorkflowBlockActionPerformanceReviewReviewerType[]
): WorkflowPerformanceReviewReviewerTypeFormData[] => {
  return reviewerTypes
    .map((reviewerType) => {
      const reviewerTypeValue = reviewerType.reviewer_type ?? reviewerType.type;
      if (!reviewerTypeValue) return null;

      const supportsAnonymous = isAnonymousSupportedReviewerType(reviewerTypeValue);
      const anonymousThreshold = reviewerType.anonymous_threshold ?? 0;
      const isAnonymous = supportsAnonymous
        ? (reviewerType.is_anonymous ?? anonymousThreshold > 0)
        : false;
      const normalizedAnonymousThreshold = supportsAnonymous
        ? isAnonymous
          ? Math.max(anonymousThreshold, ANONYMOUS_THRESHOLD_MIN)
          : 0
        : 0;

      return {
        id: reviewerType.id ?? null,
        reviewer_type: reviewerTypeValue,
        title: reviewerType.title,
        form_template_id: reviewerType.form_template_id,
        is_enabled: reviewerType.is_enabled ?? true,
        is_anonymous: isAnonymous,
        anonymous_threshold: normalizedAnonymousThreshold,
        participants_rule_groups: getWorkflowRuleGroups(
          reviewerType.participants_rule_groups,
          reviewerType.participants_rules
        ),
        invitation_subject: reviewerType.invitation_subject,
        invitation_body: reviewerType.invitation_body,
      };
    })
    .filter((reviewerType) => reviewerType != null);
};

const getSelfReviewerType = (isEnabled: boolean): WorkflowPerformanceReviewReviewerTypeFormData => {
  return {
    id: null,
    reviewer_type: ApiPerformanceReviewReviewerTypes.myself,
    title: null,
    form_template_id: null,
    is_enabled: isEnabled,
    is_anonymous: false,
    anonymous_threshold: 0,
    participants_rule_groups: [],
    invitation_subject: null,
    invitation_body: null,
  };
};

export const getDefaultReviewerTypes = (): WorkflowPerformanceReviewReviewerTypeFormData[] => {
  return [getSelfReviewerType(true)];
};
